import type { PolicyPriceDebug } from '../components/pricing/PolicySignalsPanel';
import { api } from '../services/api';

export interface BuylistSubmissionSummary {
  id: string;
  referenceCode: string | null;
  status: string;
  quotedOfferMin: number;
  quotedOfferMax: number;
  finalOffer: number | null;
  /** Offer dollars of checked-in lines that still count toward the final offer. */
  valueReceived?: number | null;
  /**
   * Plain-text offer summary (final offer, value received, accepted lines,
   * receive change list). Same body as the offer email.
   */
  customerFeedbackText?: string | null;
  amountPaid: number | null;
  marketTotal: number | null;
  insuredValue: number | null;
  payoutMethod: string | null;
  payoutStatus: string;
  shippingTracking: string | null;
  labelCarrier: string | null;
  labelService: string | null;
  labelUrl: string | null;
  /** EasyPost USPS Label Broker QR image URL (null when unsupported / not yet created). */
  labelQrUrl?: string | null;
  labelPurchasedAt: string | null;
  expiresAt: string | null;
  submittedAt: string | null;
  receivedAt: string | null;
  customerId: string | null;
  /** website | manapool | local | facebook */
  contactSource?: string | null;
}

export interface BuylistSubmissionItem {
  id: string;
  mtgjsonUuid?: string | null;
  cardName: string;
  setCode: string | null;
  cardNumber: string | null;
  condition: string;
  finish: string;
  quantity: number;
  /** Parsed/declared snapshot. Payable ``quantity`` follows received. */
  expectedQuantity?: number | null;
  /** Copies in hand, 0…expected. */
  receivedQuantity?: number | null;
  quotedUnitOffer: number;
  quotedLineTotal: number;
  marketUnitPrice: number | null;
  marketLineTotal: number | null;
  gradedCondition: string | null;
  gradedUnitOffer: number | null;
  gradedLineTotal: number | null;
  included: boolean;
  status: string;
  imageUrl: string | null;
  failReason: string | null;
  /** ISO timestamp when staff checked the physical card; null = unchecked. */
  lineCheckedAt?: string | null;
  /** Present on corrected lines that replaced a wrong submitted card. */
  replacesItemId?: string | null;
  /** Live unit markets from intake-preview pricing (null when missing). */
  tcgLow?: number | null;
  tcgMarket?: number | null;
  manapoolMarket?: number | null;
  /** MTGJSON Card Kingdom buylist (finish-aware; same path as Add Inventory). */
  ckBuylist?: number | null;
  /**
   * Live intake policy unit (soft high). Null when the quote has no price —
   * not a stored variant policy_price and not coerced to 0.
   */
  policyPrice?: number | null;
  policyPriceDebug?: PolicyPriceDebug | null;
  /**
   * Catalog finishes with TcgplayerSkus (plus normal). Null until market
   * enrichment stamps guardrails.
   */
  availableFinishes?: string[] | null;
  /** requested | auto_sku | ambiguous | unavailable */
  finishResolveSource?: string | null;
  /** True when accept must not mint this line as-is. */
  finishBlocked?: boolean;
  /** Foil and etched both/neither — staff must confirm finish. */
  finishNeedsManualPick?: boolean;
  finishBlockMessage?: string | null;
}

export interface LabelReadiness {
  ready: boolean;
  missing: string[];
  sellerComplete: boolean;
  destinationComplete: boolean;
  destination?: Record<string, string>;
  phoneRecommended?: boolean;
  carrier: string;
}

/**
 * One append-only receive edit. `createdAt` is UTC; render with `formatInstant`.
 * Product spellings: Rejected / unmatched / superseded.
 * `lineStatus` keeps the stored code (`rejected_on_grading`, `unmatched`, `superseded`).
 * `offerDelta` is the payable-offer change in dollars (0 is meaningful for condition/finish).
 */
export interface BuylistChangeEvent {
  id: string;
  createdAt: string;
  itemId: string | null;
  cardName: string;
  cardNumber: string | null;
  kind: string;
  before: string | null;
  after: string | null;
  offerDelta: number | null;
  lineStatus?: string | null;
}

export interface BuylistSubmissionDetail extends BuylistSubmissionSummary {
  items: BuylistSubmissionItem[];
  /** Oldest-first receive audit. Present on admin GET and line-mutation responses. */
  changeEvents?: BuylistChangeEvent[];
  customerEmail?: string | null;
  customerName?: string | null;
  payoutDetails?: Record<string, string> | null;
  defaultInsuredValue?: number;
  estimatedWeightOz?: number;
  sellerName?: string | null;
  sellerStreet1?: string | null;
  sellerStreet2?: string | null;
  sellerCity?: string | null;
  sellerState?: string | null;
  sellerZip?: string | null;
  sellerCountry?: string | null;
  sellerPhone?: string | null;
  easypostShipmentId?: string | null;
  labelReadiness?: LabelReadiness;
  labelRateDollars?: number;
  purchaseId?: string | null;
}

export interface BuylistListResponse {
  items: BuylistSubmissionSummary[];
  total: number;
  badgeCount: number;
}

export interface ApproveBuylistPayload {
  seller: {
    name: string;
    street1: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    phone?: string;
  };
  label_carrier?: 'usps' | 'ups' | 'fedex';
  insured_value?: number;
  weight_oz?: number;
  length_in?: number;
  width_in?: number;
  height_in?: number;
  easypost_shipment_id?: string;
  easypost_rate_id?: string;
  confirm_rate_dollars?: number;
  confirm_total_dollars?: number;
}

export interface LabelRateOption {
  shipmentId: string;
  rateId: string;
  carrier: string;
  service: string;
  serviceClass: 'ground' | 'three_day' | string;
  displayName: string;
  rateDollars: number;
  insuranceDollars: number;
  insuranceFeeDollars: number;
  totalDollars: number;
  currency: string;
}

export interface LabelQuoteResponse {
  options: LabelRateOption[];
  messages?: string[];
  shipmentId?: string | null;
  rateId?: string | null;
  carrier?: string | null;
  service?: string | null;
  rateDollars?: number | null;
  insuranceDollars: number;
  insuranceFeeDollars: number;
  totalDollars?: number | null;
  currency: string;
  weightOz: number;
  lengthIn: number;
  widthIn: number;
  heightIn: number;
  labelReadiness: LabelReadiness;
  submission: BuylistSubmissionDetail;
}

export async function listBuylistSubmissions(status?: string): Promise<BuylistListResponse> {
  const params = status ? { status } : {};
  const { data } = await api.get<BuylistListResponse>('/admin/buylist/submissions', { params });
  return data;
}

export async function getBuylistSubmission(id: string): Promise<BuylistSubmissionDetail> {
  const { data } = await api.get<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}`);
  return data;
}

export interface BuylistContactUpdate {
  email?: string;
  name?: string;
  contact_source?: string | null;
  clear_contact_source?: boolean;
}

export async function updateBuylistContact(
  id: string,
  payload: BuylistContactUpdate,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.patch<BuylistSubmissionDetail>(
    `/admin/buylist/submissions/${id}/contact`,
    payload,
  );
  return data;
}

export async function createBuylistFromParse(form: FormData): Promise<BuylistSubmissionDetail & { jobId?: string }> {
  const { data } = await api.post('/admin/buylist/from-parse', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function quoteBuylistLabel(
  id: string,
  payload: ApproveBuylistPayload,
): Promise<LabelQuoteResponse> {
  const { data } = await api.post<LabelQuoteResponse>(`/admin/buylist/${id}/label-quote`, payload);
  return data;
}

export async function approveBuylistSubmission(
  id: string,
  payload: ApproveBuylistPayload,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(`/admin/buylist/${id}/approve`, payload);
  return data;
}

/** Authenticated download of the EasyPost postage label (PDF/PNG). */
export async function downloadBuylistLabel(id: string, filenameHint?: string): Promise<void> {
  const response = await api.get(`/admin/buylist/${id}/label`, {
    params: { redirect: false },
    responseType: 'blob',
  });
  const contentType = String(response.headers['content-type'] || 'application/pdf');
  const ext = contentType.toLowerCase().includes('pdf') ? 'pdf' : 'png';
  const blobUrl = URL.createObjectURL(response.data);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = `${filenameHint || id}-shipping-label.${ext}`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

export async function ensureBuylistLabelQr(
  id: string,
): Promise<{ labelQrUrl: string; created: boolean; submission: BuylistSubmissionDetail }> {
  const { data } = await api.post<{
    labelQrUrl: string;
    created: boolean;
    submission: BuylistSubmissionDetail;
  }>(`/admin/buylist/${id}/label-qr`);
  return data;
}

export async function markBuylistReceived(
  id: string,
  amountPaid?: number,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/receive`, {
    amount_paid: amountPaid,
  });
  return data;
}

export async function setBuylistAmountPaid(id: string, amountPaid: number): Promise<BuylistSubmissionDetail> {
  const { data } = await api.patch<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/amount-paid`, {
    amount_paid: amountPaid,
  });
  return data;
}

export async function markBuylistOfferReady(id: string): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/offer-ready`);
  return data;
}

export async function acceptBuylistSubmission(id: string): Promise<BuylistSubmissionDetail & { purchaseId?: string }> {
  const { data } = await api.post(`/admin/buylist/submissions/${id}/accept`);
  return data;
}

export async function rejectBuylistSubmission(id: string, reason: string): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/reject`, { reason });
  return data;
}

export async function recordBuylistPayout(
  id: string,
  payoutStatus: 'sent' | 'completed',
  notes?: string,
  amountPaid?: number,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/payout`, {
    payout_status: payoutStatus,
    notes,
    amount_paid: amountPaid,
  });
  return data;
}

export interface BuylistItemUpdate {
  item_id: string;
  graded_condition?: string;
  status?: string;
  graded_unit_offer?: number;
  quantity?: number;
  /** Step received by this many copies (typically ±1). Does not change expected. */
  received_delta?: number;
  received_quantity?: number;
  /** Logged correction of the declared snapshot. */
  expected_quantity?: number;
  finish?: string;
  included?: boolean;
  mtgjson_uuid?: string;
  card_name?: string;
  set_code?: string;
  card_number?: string;
  fail_reason?: string;
  line_checked?: boolean;
}

export async function gradeBuylistItems(
  id: string,
  updates: BuylistItemUpdate[],
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.patch<BuylistSubmissionDetail>(`/admin/buylist/submissions/${id}/items`, {
    updates,
  });
  return data;
}

export interface BuylistReplaceItemPayload {
  /** Magic catalog id. Omit when ``tcgplayer_id`` is set. */
  mtgjson_uuid?: string;
  /** Pokémon / Lorcana TCGPlayer product id. */
  tcgplayer_id?: string;
  /** Canonical product line (Pokemon, Lorcana) required with ``tcgplayer_id``. */
  product_line?: string;
  /** Staff language for an English TCGCSV printing. Japanese printings stay JA. */
  language?: string;
  card_name: string;
  set_code?: string;
  card_number?: string;
  condition?: string;
  finish?: string;
  quantity?: number;
  image_url?: string;
}

/** Supersede a wrong line and add a corrected priced line for offer/COGS/intake. */
export async function replaceBuylistItem(
  submissionId: string,
  itemId: string,
  payload: BuylistReplaceItemPayload,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(
    `/admin/buylist/submissions/${submissionId}/items/${itemId}/replace`,
    payload,
  );
  return data;
}

export interface BuylistMatchItemPayload {
  /** Magic catalog id. Omit when ``tcgplayer_id`` is set. */
  mtgjson_uuid?: string;
  /** Pokémon / Lorcana TCGPlayer product id. */
  tcgplayer_id?: string;
  /** Canonical product line (Pokemon, Lorcana) required with ``tcgplayer_id``. */
  product_line?: string;
  /** Staff language for an English TCGCSV printing. Japanese printings stay JA. */
  language?: string;
  card_name: string;
  set_code?: string;
  card_number?: string;
  condition?: string;
  finish?: string;
  quantity?: number;
  image_url?: string;
}

/** Match an unmatched (or wrong) line in place via Add Inventory product picker. */
export async function matchBuylistItem(
  submissionId: string,
  itemId: string,
  payload: BuylistMatchItemPayload,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(
    `/admin/buylist/submissions/${submissionId}/items/${itemId}/match`,
    payload,
  );
  return data;
}

/** Drop an unmatched/rejected line so it is not intaken and totals update. */
export async function removeBuylistItem(
  submissionId: string,
  itemId: string,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.delete<BuylistSubmissionDetail>(
    `/admin/buylist/submissions/${submissionId}/items/${itemId}`,
  );
  return data;
}

export interface BuylistSplitPart {
  /** This part's expected count. Parts must sum to the line expected qty. */
  quantity: number;
  /** Copies of this part already in hand. Required when the line is partial. */
  received_quantity?: number;
  condition?: string;
  finish?: string;
}

export interface BuylistSplitItemPayload {
  parts: BuylistSplitPart[];
}

/** Split a qty≥2 line into sibling lines with their own condition/finish/qty. */
export async function splitBuylistItem(
  submissionId: string,
  itemId: string,
  payload: BuylistSplitItemPayload,
): Promise<BuylistSubmissionDetail> {
  const { data } = await api.post<BuylistSubmissionDetail>(
    `/admin/buylist/submissions/${submissionId}/items/${itemId}/split`,
    payload,
  );
  return data;
}
